import type { FormEvent } from 'react'
import { useCallback } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'

import { move } from '@jotta/grpc-connect-openapi/fileQuery'
import { showToast } from '@jotta/ui/ToastProvider'
import { renamePath } from '@jotta/utils/pathUtils'

import { nameForServer } from '../normalizePathItemName'
import type { PathItemStore } from '../PathItemStore'
import { validatePath } from '../validatePath'

export const useRenamePathSubmitHandler = (
  store: PathItemStore,
  newName: string,
) => {
  const item = store.model
  const parent = store.folder

  // The API rejects trailing whitespace: validate and send it trimmed
  const serverName = nameForServer(newName)
  const validatePathErrorMessage = validatePath(serverName)
  const mutationErrorMessage = t`Rename failed. Please check that the name is valid and try again`
  const path = item.path
  const toPath = renamePath(path, serverName)
  const onCancel = useCallback(() => {
    item.reset()
    parent.actions.cancelActiveAction()
  }, [item, parent])
  const { mutate, variables, isError } = useMutation(move, {
    throwOnError: false,
    onSuccess: () => {
      parent.removeItem(item.path)
      item.path = toPath
      item.name = serverName
      item.submit()
      parent.addEntry(store)
      onCancel()
    },
    onError: () =>
      showToast({
        message: mutationErrorMessage,
        hasCloseButton: true,
      }),
  })

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (toPath === path) {
        // Nothing changed once trimmed
        onCancel()
        return
      }
      mutate({
        fromPath: path,
        toPath: toPath,
      })
    },
    [mutate, onCancel, path, toPath],
  )

  return {
    errorMessage:
      validatePathErrorMessage ||
      (isError && variables?.toPath === toPath && mutationErrorMessage),
    onSubmit,
    onCancel,
  }
}
